import React, { useMemo } from 'react';
import './tiyong.css';
import { FiveElement, Hexagram, hexagramLines, trigramElement, trigramNameZh } from '../core/meihua';
import { hexagramName, trigramImage, trigramSymbol } from '../core/gua64';
import {
  ActorSide,
  ELEMENT_THEME,
  FOCUS_META,
  FOCUS_ORDER,
  FocusItem,
  FocusKey,
  FocusView,
  KE_CYCLE,
  SHENG_CYCLE,
  TiYong,
  TiYongReading,
  TrigramPosition,
  buildFocusView,
  buildReading,
  elementTag,
  elementTheme,
  movingYaoMark,
  positionLabel,
  trigramAt
} from '../core/tiyong';

/* =====================================================================
 * 卦象图
 * 画法依standard：每爻一条横线，阳爻一整条（九），阴爻中断为两段（六）；
 * 上卦在上、下卦在下；动爻标红并注 ○（老阳）/ ×（老阴）；图下系卦名。
 * ===================================================================== */

export type ChartRoleMode = 'ben' | 'hu' | 'bian';
type RoleKey = 'ti' | 'yong' | 'bian' | 'hu';

const ROLE: Record<RoleKey, { label: string; title: string }> = {
  ti: { label: '体', title: '体卦：不动之卦，代表自身、主方、内在条件' },
  yong: { label: '用', title: '用卦：动爻所在之卦，代表对方、所测之事、外部环境' },
  bian: { label: '变', title: '变卦：用卦所变之卦，主事之终' },
  hu: { label: '互', title: '互卦：本卦二三四爻为下互、三四五爻为上互，主事之中' }
};

const YAO_POS = ['初', '二', '三', '四', '五', '上'];

const roleForGroup = (mode: ChartRoleMode, pos: TrigramPosition, ti: TiYong): RoleKey => {
  if (mode === 'ben') return pos === ti.usePos ? 'yong' : 'ti';
  if (mode === 'bian') return pos === ti.usePos ? 'bian' : 'ti';
  return 'hu';
};

export interface ChartRefTag {
  color: string;
  bg: string;
  key: string;
  desc: string;
}

interface TiYongChartProps {
  hex: Hexagram;
  mode: ChartRoleMode;
  ti: TiYong;
  size?: 'sm' | 'md';
  /** 该卦上下两支对体卦的作用（互卦 / 变卦用） */
  refs?: Partial<Record<TrigramPosition, ChartRefTag>>;
  selected?: boolean;
  onSelect?: () => void;
}

export const TiYongChart: React.FC<TiYongChartProps> = ({
  hex, mode, ti, size = 'md', refs, selected = false, onSelect
}) => {
  const lines = hexagramLines(hex);
  const movingLine = mode === 'hu' ? -1 : ti.movingLine;

  const groups: { pos: TrigramPosition; idxs: number[] }[] = [
    { pos: 'upper', idxs: [5, 4, 3] },
    { pos: 'lower', idxs: [2, 1, 0] }
  ];

  return (
    <div
      className={['tyc', 'tyc--' + size, selected ? 'tyc--on' : '', onSelect ? 'tyc--tap' : ''].filter(Boolean).join(' ')}
      onClick={onSelect}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={onSelect ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } } : undefined}
      aria-pressed={onSelect ? selected : undefined}
      aria-label={hexagramName(hex)}
    >
      {groups.map((g) => {
        const tri = trigramAt(hex, g.pos);
        const role = roleForGroup(mode, g.pos, ti);
        const spec = ROLE[role];
        const theme = elementTheme(trigramElement(tri));
        const tag = refs && refs[g.pos];
        return (
          <div key={g.pos} className={'tyc-group tyc-group--' + role}>
            <div className="tyc-head">
              <span className={'tyc-role tyc-role--' + role} title={spec.title}>{spec.label}</span>
              <span className="tyc-tname" title={trigramImage(tri) + '·' + positionLabel(g.pos)}>
                {trigramNameZh(tri)}
                <span className="tyc-sym">{trigramSymbol(tri)}</span>
              </span>
              <span className="tyc-el" style={{ color: theme.fg, background: theme.bg, borderColor: theme.ring }}>
                {trigramElement(tri)}
              </span>
              <span className="tyc-pos">{positionLabel(g.pos)}</span>
              {tag && (
                <span className="tyc-rel" title={tag.desc} style={{ color: tag.color, background: tag.bg }}>
                  {tag.key}
                </span>
              )}
            </div>
            <div className="tyc-lines">
              {g.idxs.map((idx) => {
                const isMoving = movingLine === idx + 1;
                const isYang = lines[idx] === 'yang';
                const mark = !isMoving ? '' : mode === 'ben' ? movingYaoMark(lines[idx]) : '变';
                return (
                  <div
                    key={idx}
                    className={'tyc-lrow' + (isMoving ? ' tyc-lrow--moving' : '')}
                    title={'第' + (idx + 1) + '爻（' + (isYang ? '阳爻' : '阴爻') + '）' + (isMoving ? '发动' : '')}
                  >
                    <span className="tyc-yao">{YAO_POS[idx]}</span>
                    {isYang ? (
                      <span className="tyc-line"><span className="tyc-bar" /></span>
                    ) : (
                      <span className="tyc-line tyc-line--break"><span className="tyc-bar" /><span className="tyc-bar" /></span>
                    )}
                    <span className="tyc-mark">{mark}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <div className="tyc-foot">{hexagramName(hex)}</div>
    </div>
  );
};

/* =====================================================================
 * 五行生克环
 * ===================================================================== */

export interface RingLink {
  elem: FiveElement;
  kind: '生' | '克' | '比';
  color: string;
  actor: ActorSide;
}

interface WuxingRingProps {
  bodyElem: FiveElement;
  links: RingLink[];
  otherLabel?: string;
  size?: number;
}

export const WuxingRing: React.FC<WuxingRingProps> = ({ bodyElem, links, otherLabel = '用', size = 168 }) => {
  const c = size / 2;
  const r = size / 2 - 26;
  const at = (i: number) => {
    const a = (-90 + i * 72) * (Math.PI / 180);
    return { x: c + r * Math.cos(a), y: c + r * Math.sin(a) };
  };
  const idxOf = (e: FiveElement) => SHENG_CYCLE.indexOf(e);
  const bIdx = idxOf(bodyElem);

  const shengEdges: [number, number][] = SHENG_CYCLE.map((_, i) => [i, (i + 1) % 5]);
  const keEdges: [number, number][] = KE_CYCLE.map((from, i) => [idxOf(from), idxOf(KE_CYCLE[(i + 1) % 5])]);

  const hot = links.map((l) => {
    const oIdx = idxOf(l.elem);
    const from = l.actor === 'other' ? oIdx : bIdx;
    const to = l.actor === 'other' ? bIdx : oIdx;
    return { ...l, from, to };
  });

  const isHot = (a: number, b: number, kind: '生' | '克') =>
    hot.find((h) => h.kind === kind && h.from === a && h.to === b) ||
    hot.find((h) => h.kind === kind && h.from === b && h.to === a && kind === '生');

  const edge = (a: number, b: number, kind: '生' | '克', key: string) => {
    const h = isHot(a, b, kind);
    const p1 = at(a);
    const p2 = at(b);
    return (
      <line
        key={key}
        x1={p1.x}
        y1={p1.y}
        x2={p2.x}
        y2={p2.y}
        stroke={h ? h.color : '#DCDCE1'}
        strokeWidth={h ? 2.8 : 1}
        strokeDasharray={kind === '克' ? '4 4' : undefined}
        markerEnd={h ? 'url(#ty-arrow)' : undefined}
      />
    );
  };

  const hotColor = hot.length ? hot[0].color : '#c9a227';
  const otherIdxs = links.map((l) => idxOf(l.elem));

  return (
    <svg className="ty-ring" width={size} height={size} viewBox={'0 0 ' + size + ' ' + size} role="img" aria-label="五行生克环">
      <defs>
        <marker id="ty-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="4.6" markerHeight="4.6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={hotColor} />
        </marker>
      </defs>
      {shengEdges.map(([a, b], i) => edge(a, b, '生', 's' + i))}
      {keEdges.map(([a, b], i) => edge(a, b, '克', 'k' + i))}
      {SHENG_CYCLE.map((e, i) => {
        const p = at(i);
        const theme = ELEMENT_THEME[e];
        const onBody = i === bIdx;
        const isOther = otherIdxs.indexOf(i) >= 0;
        const emph = onBody || isOther;
        const roleLabel = onBody && isOther ? '体' + otherLabel : onBody ? '体' : isOther ? otherLabel : '';
        return (
          <g key={e}>
            <circle
              cx={p.x}
              cy={p.y}
              r={emph ? 15 : 11.5}
              fill={emph ? theme.bg : '#12141d'}
              stroke={onBody ? '#f2eee3' : isOther ? hotColor : theme.ring}
              strokeWidth={emph ? 2 : 1}
            />
            <text x={p.x} y={p.y + 4} textAnchor="middle" className="ty-ring-text" fill={theme.fg}>{e}</text>
            {roleLabel && (
              <text x={p.x} y={p.y - 20} textAnchor="middle" className="ty-ring-role" fill={onBody ? '#f2eee3' : hotColor}>
                {roleLabel}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

/* =====================================================================
 * 生克判读抽屉 —— 默认收起；展开后按 本卦 / 互卦 / 变卦 切换
 * ===================================================================== */

const KIND_GLYPH: Record<string, string> = {
  生体: '←', 克体: '←', 体生: '→', 体克: '→', 比和: '＝',
  用生体: '←', 用克体: '←', 体生用: '→', 体克用: '→'
};

const itemSummary = (view: FocusView): string =>
  view.items.map((i) => i.role + i.tag + '·' + i.key).join('，');

const ItemRow: React.FC<{ item: FocusItem }> = ({ item }) => {
  const theme = elementTheme(item.elem);
  return (
    <div className="ty-item">
      <span className="ty-item-glyph" style={{ color: item.color, background: item.bg }}>{KIND_GLYPH[item.key] || '＝'}</span>
      <span className="ty-item-role" style={{ background: item.color }}>{item.role}</span>
      <span className="ty-item-name">{trigramNameZh(item.trigram)}</span>
      <span className="ty-item-el" style={{ color: theme.fg, background: theme.bg }}>{elementTag(item.trigram)}</span>
      <span className="ty-item-pos">{positionLabel(item.pos)}</span>
      <span className="ty-item-key" style={{ color: item.color }}>{item.key}</span>
      {item.grade && (
        <span className="ty-item-grade" style={{ color: item.color, borderColor: item.color }}>{item.grade}</span>
      )}
      <span className="ty-item-desc">{item.desc}</span>
    </div>
  );
};

interface RelationDrawerProps {
  reading: TiYongReading;
  focus: FocusKey;
  open: boolean;
  onToggle: () => void;
  onFocus: (f: FocusKey) => void;
}

export const RelationDrawer: React.FC<RelationDrawerProps> = ({ reading, focus, open, onToggle, onFocus }) => {
  const view = useMemo(() => buildFocusView(reading, focus), [reading, focus]);
  const { tiYong } = reading;
  const bodyTheme = elementTheme(trigramElement(tiYong.body));
  const focusHex =
    focus === 'original' ? reading.original : focus === 'mutual' ? reading.mutual : reading.changed;

  return (
    <section className={'ty-drawer' + (open ? ' ty-drawer--open' : '')}>
      <button type="button" className="ty-drawer-head" onClick={onToggle} aria-expanded={open}>
        <span className="ty-drawer-icon" style={{ background: view.toneColor }}>生克</span>
        <span className="ty-drawer-text">
          <span className="ty-drawer-title">
            生克判读 · {view.meta.label}
            <span className="ty-drawer-short">{view.meta.short}</span>
          </span>
          <span className="ty-drawer-sum">
            {hexagramName(focusHex)}：{itemSummary(view)}
            <span className="ty-drawer-sum-tone" style={{ color: view.toneColor }}> · {view.tone}</span>
          </span>
        </span>
        <span className="ty-drawer-tone" style={{ color: view.toneColor }}>{view.tone}</span>
        <span className="ty-drawer-chev" aria-hidden>{open ? '收起 ▾' : '展开 ▸'}</span>
      </button>

      {open && (
        <div className="ty-drawer-body">
          <div className="ty-seg" role="tablist">
            {FOCUS_ORDER.map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={k === focus}
                className={'ty-seg-btn' + (k === focus ? ' ty-seg-btn--on' : '')}
                onClick={() => onFocus(k)}
              >
                {FOCUS_META[k].label}
                <span className="ty-seg-sub">{FOCUS_META[k].short}</span>
              </button>
            ))}
          </div>

          <p className="ty-hint">{view.meta.hint}</p>

          <div className="ty-head"> 
            <div className="ty-body-pillar">
              <span className="ty-end-role ty-end-role--ti">体</span>
              <span className="ty-end-name">{trigramNameZh(tiYong.body)}</span>
              <span className="ty-end-el" style={{ color: bodyTheme.fg, background: bodyTheme.bg }}>{elementTag(tiYong.body)}</span>
              <span className="ty-end-pos">{positionLabel(tiYong.bodyPos)} · 不动</span>
            </div>
            <div className="ty-items">
              {view.items.map((it) => <ItemRow key={it.role} item={it} />)}
            </div>
          </div>

          <div className="ty-cols">
            <WuxingRing
              bodyElem={trigramElement(tiYong.body)}
              otherLabel={focus === 'original' ? '用' : focus === 'mutual' ? '互' : '变'}
              links={view.items.map((i) => ({ elem: i.elem, kind: i.kind, color: i.color, actor: i.actor }))}
            />
            <div className="ty-facts">
              <div className="ty-fact">
                <span className="ty-fact-k">动爻</span>
                <span className="ty-fact-v">
                  第{tiYong.movingLine}爻 · {tiYong.yaoName}（{positionLabel(tiYong.movingIn)}）
                  <span className="ty-fact-mark">{movingYaoMark(hexagramLines(reading.original)[tiYong.movingLine - 1])}</span>
                </span>
              </div>
              <div className="ty-fact">
                <span className="ty-fact-k">体用</span>
                <span className="ty-fact-v">
                  体{elementTag(tiYong.body)}（{positionLabel(tiYong.bodyPos)}） ／ 用{elementTag(tiYong.use)}（{positionLabel(tiYong.usePos)}） → {reading.pair.key}
                  <span className="ty-fact-grade" style={{ color: reading.pair.color }}> {reading.pair.tone}</span>
                </span>
              </div>
              <div className="ty-fact">
                <span className="ty-fact-k">{view.meta.label}</span>
                <span className="ty-fact-v ty-fact-v--chips">
                  {view.items.map((i) => (
                    <span key={i.role} className="ty-chip" style={{ color: i.color, background: i.bg, borderColor: i.color }} title={i.desc}>
                      {i.role}{i.tag} · {i.key}
                    </span>
                  ))}
                  <span className="ty-chip ty-chip--score" style={{ color: view.toneColor, borderColor: view.toneColor }}>{view.tone}</span>
                </span>
              </div>
              <div className="ty-fact">
                <span className="ty-fact-k">卦名</span>
                <span className="ty-fact-v">
                  本卦 {hexagramName(reading.original)} · 互卦 {hexagramName(reading.mutual)} · 变卦 {hexagramName(reading.changed)}
                </span>
              </div>
              {reading.ling && (
                <div className="ty-fact">
                  <span className="ty-fact-k">月令</span>
                  <span className="ty-fact-v ty-fact-v--chips">
                    <span className="ty-chip ty-chip--plain">{reading.yueling}</span>
                    <span className="ty-chip" style={{ color: reading.ling.color, background: reading.ling.bg, borderColor: reading.ling.color }} title={reading.ling.desc}>
                      {reading.ling.label}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>

          <p className="ty-detail">{view.detail}</p>

          <div className="ty-advice" style={{ borderColor: view.items[0] ? view.items[0].color : '#D9D9DE' }}>
            <span className="ty-advice-k" style={{ background: view.items[0] ? view.items[0].color : '#8E8E93' }}>断</span>
            <span className="ty-advice-v">{view.advice}</span>
          </div>

          <div className="ty-summary">{reading.summary}</div>

          <div className="ty-legend">
            <span className="ty-legend-item">阳爻一整条（九）；阴爻中断为两段（六）。红者为动爻：○ 老阳、× 老阴</span>
            <span className="ty-legend-item">吉凶口诀：用生体大吉、体用比和吉、体克用小吉、体生用小凶、用克体大凶</span>
            <span className="ty-legend-item">环中实线＝相生（木→火→土→金→水），虚线＝相克（木克土、土克水、水克火、火克金、金克木）</span>
            <span className="ty-legend-item">← 表示该卦作用于体卦，→ 表示体卦作用于该卦；互卦、变卦皆为「用」，体卦始终只有一个</span>
          </div>
        </div>
      )}
    </section>
  );
};

/* =====================================================================
 * 三盘并排 —— 点选任意一卦，下方判读随之切换
 * ===================================================================== */

interface HexagramTriadProps {
  original: Hexagram;
  mutual: Hexagram;
  changed: Hexagram;
  reading: TiYongReading;
  focus: FocusKey;
  onFocus: (f: FocusKey) => void;
  size?: 'sm' | 'md';
}

export const HexagramTriad: React.FC<HexagramTriadProps> = ({ original, mutual, changed, reading, focus, onFocus, size = 'md' }) => {
  const ti = reading.tiYong;
  const cells: { key: FocusKey; hex: Hexagram; mode: ChartRoleMode; note: string }[] = [
    { key: 'original', hex: original, mode: 'ben', note: '体用在此定' },
    { key: 'mutual', hex: mutual, mode: 'hu', note: '卦中互见' },
    { key: 'changed', hex: changed, mode: 'bian', note: '用卦所变' }
  ];
  const bodyTag = elementTag(ti.body);

  return (
    <div className={'ty-triad' + (size === 'sm' ? ' ty-triad--sm' : '')}>
      {cells.map((cell) => {
        const meta = FOCUS_META[cell.key];
        const active = focus === cell.key;
        let foot: React.ReactNode;
        if (cell.key === 'original') {
          foot = (
            <span className="ty-cell-chip" style={{ color: reading.pair.color, background: reading.pair.bg }}>
              {reading.pair.key} · {reading.pair.tone}
            </span>
          );
        } else if (cell.key === 'mutual') {
          foot = (
            <>
              <span className="ty-cell-note-sm">对体卦 {bodyTag}：</span>
              {reading.mutualRefs.map((r, i) => (
                <span key={i} className="ty-cell-chip" style={{ color: r.ref.color, background: r.ref.bg }}>
                  {(r.pos === 'upper' ? '互上' : '互下') + ' ' + r.tag + ' · ' + r.ref.key}
                </span>
              ))}
            </>
          );
        } else {
          foot = (
            <>
              <span className="ty-cell-note-sm">对体卦 {bodyTag}：</span>
              <span className="ty-cell-chip" style={{ color: reading.changedRef.ref.color, background: reading.changedRef.ref.bg }}>
                {(reading.changedRef.pos === 'upper' ? '变上' : '变下') + ' ' + reading.changedRef.tag + ' · ' + reading.changedRef.ref.key}
              </span>
            </>
          );
        }
        return (
          <div key={cell.key} className={'ty-cell' + (active ? ' ty-cell--on' : '')}>
            <div className="ty-cell-head">
              <span className="ty-cell-title">
                {meta.label}
                <span className="ty-cell-note">{meta.short} · {cell.note}</span>
              </span>
            </div>
            <TiYongChart
              hex={cell.hex}
              mode={cell.mode}
              ti={ti}
              size={size}
              selected={active}
              onSelect={() => onFocus(cell.key)}
              refs={
                cell.mode === 'hu'
                  ? { upper: reading.mutualRefs[0].ref, lower: reading.mutualRefs[1].ref }
                  : cell.mode === 'bian'
                    ? { [reading.changedRef.pos]: reading.changedRef.ref }
                    : undefined
              }
            />
            <div className="ty-cell-foot">{foot}</div>
          </div>
        );
      })}
    </div>
  );
};

/* =====================================================================
 * 整体：三盘 + 可收起的判读抽屉
 * ===================================================================== */

interface TiYongBoardProps {
  original: Hexagram;
  mutual: Hexagram;
  changed: Hexagram;
  yueling?: string | null;
  size?: 'sm' | 'md';
  defaultOpen?: boolean;
}

export const TiYongBoard: React.FC<TiYongBoardProps> = ({
  original, mutual, changed, yueling, size = 'md', defaultOpen = false
}) => {
  const reading = useMemo(
    () => buildReading(original, mutual, changed, yueling ?? null),
    [original, mutual, changed, yueling]
  );
  const [focus, setFocus] = React.useState<FocusKey>('changed');
  const [open, setOpen] = React.useState(defaultOpen);

  if (!reading) return null;

  const pick = (f: FocusKey) => {
    setFocus(f);
    setOpen(true);
  };

  return (
    <div className="ty-board">
      <HexagramTriad
        original={original}
        mutual={mutual}
        changed={changed}
        reading={reading}
        focus={focus}
        onFocus={pick}
        size={size}
      />
      <RelationDrawer
        reading={reading}
        focus={focus}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onFocus={setFocus}
      />
    </div>
  );
};
